import { useEffect, useRef, useState } from "react";
import { motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useCurrentFair } from "../lib/fairSchedule";
import {
  SERVICES,
  HOTEL_BENEFITS,
  BOOKING_URL,
  INCLUDED_INTRO,
} from "../data/site";

const EASE = [0.16, 1, 0.3, 1];
const PHONE_MAX_WIDTH = 900;

/* Phone only: the service whose row sits at the middle of the screen is highlighted.
   Before the list reaches the middle the first row is active; after the last row, the last stays. */
function useScrollHighlight(count) {
  const rowRefs = useRef([]);
  const [active, setActive] = useState(null);
  const { scrollY } = useScroll();

  const update = () => {
    if (window.innerWidth > PHONE_MAX_WIDTH) {
      setActive(null);
      return;
    }
    const rows = rowRefs.current.filter(Boolean);
    if (rows.length === 0) return;
    const mid = window.innerHeight / 2;
    const rects = rows.map((el) => el.getBoundingClientRect());
    let next = count - 1;
    if (rects[0].top > mid) {
      next = 0;
    } else {
      const hit = rects.findIndex((r) => r.top <= mid && r.bottom > mid);
      if (hit !== -1) next = hit;
      else if (rects[rects.length - 1].bottom > mid) {
        // in a gap between rows: pick the nearest edge
        next = rects.findIndex((r) => r.top > mid);
        if (next > 0 && mid - rects[next - 1].bottom < rects[next].top - mid) next -= 1;
      }
    }
    setActive(next);
  };

  useMotionValueEvent(scrollY, "change", update);
  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { rowRefs, active };
}

const Arrow = ({ size = 16 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="7" y1="17" x2="17" y2="7"></line>
    <polyline points="7 7 17 7 17 17"></polyline>
  </svg>
);

export const Included = () => {
  const { rowRefs, active } = useScrollHighlight(SERVICES.length);
  const { fair, label } = useCurrentFair();
  return (
  <section id="included" className="px-5 md:px-10 py-16 md:py-24 bg-paper" data-testid="included-section">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
      <div className="lg:col-span-4">
        <div className="lg:sticky lg:top-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <span className="text-[12px] font-bold tracking-widest uppercase text-ink">
              What's included
            </span>
            <h2 className="serif text-5xl md:text-6xl uppercase tracking-normal leading-[0.9] mt-5 text-ink">
              One fee.
              <br />
              The whole week.
            </h2>
            <div className="mt-6 space-y-4 max-w-[46ch]">
              {INCLUDED_INTRO.map((para) => (
                <p className="text-[17px] font-bold leading-relaxed text-deep" key={para}>{para}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      <div className={`lg:col-span-8${active !== null ? " service-list--scroll" : ""}`} data-testid="service-list">
        {SERVICES.map((s, i) => (
          <motion.div
            className={`service-row${active === i ? " is-active" : ""}`}
            key={s.title}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            data-active={active === i ? "true" : undefined}
            tabIndex={0}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: (i % 3) * 0.08, ease: EASE }}
            data-testid={`service-row-${i + 1}`}
          >
            <span className="serif text-lg service-idx">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="serif text-2xl md:text-3xl uppercase tracking-normal leading-none pt-0.5 service-copy">{s.title}</h3>
            <p className="col-start-2 md:col-start-auto text-[17px] font-medium leading-relaxed pt-1 md:pr-16 service-copy">{s.copy}</p>
            <span className="service-arrow" aria-hidden="true">
              <Arrow />
            </span>
          </motion.div>
        ))}

        <div className="border-t border-ink/60 mt-14 pt-10">
          <h2 className="serif text-3xl md:text-5xl uppercase tracking-normal leading-[0.9] text-ink">
            Hotel benefits
          </h2>
          <p className="text-base font-medium leading-relaxed text-ink mt-4 max-w-[52ch]" data-testid="hotel-benefits-subline">
            {HOTEL_BENEFITS.subline}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-10 gap-y-8 mt-8 max-w-3xl" data-testid="hotel-benefits-columns">
            <div>
              <p className="serif text-2xl md:text-3xl uppercase tracking-normal leading-none mb-3 text-ink" data-testid="benefits-col-every-stay">With every stay</p>
              <ul className="text-base font-medium leading-relaxed text-ink">
                {HOTEL_BENEFITS.everyStay.map((b) => (
                  <li key={b} className="border-t border-ink/15 py-2">{b}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="serif text-2xl md:text-3xl uppercase tracking-normal leading-none mb-3 text-ink" data-testid="benefits-col-when-available">When available</p>
              <ul className="text-base font-medium leading-relaxed text-ink">
                {HOTEL_BENEFITS.whenAvailable.map((b) => (
                  <li key={b} className="border-t border-ink/15 py-2">{b}</li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-sm font-medium leading-relaxed mt-6 text-deep flex items-start gap-3" data-testid="hotel-benefits-note">
            <span className="text-deep text-3xl leading-none shrink-0 font-sans" aria-hidden="true">✱</span>
            <span>{HOTEL_BENEFITS.note}</span>
          </p>
        </div>

        <motion.a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener"
          data-testid="included-plan-trip-cta"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="closing-cta mt-16 md:mt-20"
        >
          <span className="flex flex-col gap-3">
            <span className="text-[11px] uppercase font-medium tracking-[0.3em] opacity-80">
              {fair ? `${fair.name} · ${fair.short}` : "Enquiries open"}
            </span>
            <span className="serif text-4xl md:text-6xl lg:text-7xl uppercase tracking-normal leading-[0.9]">
              {fair ? (
                <>
                  Going to {fair.name.split(" ")[0]}
                  <br />
                  {fair.name.split(" ").slice(1).join(" ")}?
                </>
              ) : (
                <>
                  Planning a
                  <br />
                  fair week?
                </>
              )}
            </span>
            <span className="serif text-2xl md:text-3xl mt-2">Plan your trip</span>
          </span>
          <span className="cta-circle">
            <Arrow size={28} />
          </span>
        </motion.a>
        {fair && label ? (
          <p className="cta-countdown mt-4 text-[11px] uppercase tracking-[0.25em] font-bold text-ink" data-testid="cta-countdown">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent mr-2 align-middle animate-pulse" aria-hidden="true" />
            {fair.name} {label.toLowerCase()}
          </p>
        ) : null}
      </div>
    </div>
  </section>
  );
};
